<template>
  <b-modal
    ref="modal"
    title="Replicar cadastros fiscais"
    size="lg"
    centered
    no-close-on-backdrop
    :no-close-on-esc="copiando"
    :hide-header-close="copiando"
    @hide="impedirFechamento"
    @hidden="invalidarConsulta"
  >
    <p class="text-muted">
      Escolha de qual empresa copiar os cadastros para a empresa de destino
      abaixo.
    </p>
    <fieldset :disabled="copiando || !!resultado">
      <div class="row">
        <div class="col-md-6">
          <b-form-group
            label="Empresa de origem — copiar de"
            label-for="fiscal-empresa-origem"
          >
            <EmpresaParceiroSelect
              v-model="origem"
              input-id="fiscal-empresa-origem"
              :disabled="copiando || !!resultado"
            />
          </b-form-group>
        </div>
        <div class="col-md-6">
          <b-form-group
            label="Empresa de destino — inserir em"
            label-for="fiscal-empresa-destino"
          >
            <b-form-input
              id="fiscal-empresa-destino"
              :value="destino ? nomeEmpresa(destino) : ''"
              readonly
            />
          </b-form-group>
        </div>
      </div>
      <b-alert :show="mesmaEmpresa" variant="warning">
        Escolha empresas diferentes para origem e destino.
      </b-alert>
      <b-form-group label="O que deseja replicar?" label-for="fiscal-modulo">
        <b-form-select id="fiscal-modulo" v-model="modulo" :options="modulos" />
      </b-form-group>
      <b-form-group label="Quais registros?">
        <b-form-radio-group
          v-model="modo"
          :options="modos"
          name="fiscal-modo"
        />
      </b-form-group>

      <div v-if="origem">
        <template v-if="modo === 'SELECIONADOS'">
          <b-form class="mb-3" @submit.prevent="carregarRegistros(1)">
            <b-input-group>
              <b-form-input
                v-model.trim="busca"
                placeholder="Buscar pela descrição"
                aria-label="Buscar cadastros fiscais"
              />
              <b-input-group-append>
                <b-button
                  type="submit"
                  variant="outline-primary"
                  :disabled="carregando"
                  >Buscar</b-button
                >
              </b-input-group-append>
            </b-input-group>
          </b-form>
          <b-table
            :items="registros"
            :fields="campos"
            :busy="carregando"
            small
            responsive
            show-empty
            empty-text="Nenhum cadastro encontrado na empresa de origem."
          >
            <template #cell(selecionar)="{ item }">
              <b-form-checkbox
                v-model="selecionados"
                :value="Number(item.id)"
                :disabled="
                  copiando ||
                  (selecionados.length >= 100 &&
                    !selecionados.includes(Number(item.id)))
                "
                :aria-label="`Selecionar ${item.descricao}`"
              />
            </template>
            <template #table-busy>
              <div class="text-center p-3">
                <b-spinner small /> Carregando cadastros...
              </div>
            </template>
          </b-table>
          <b-pagination
            v-if="total > 20"
            :value="pagina"
            :total-rows="total"
            :per-page="20"
            :disabled="carregando || copiando"
            @input="carregarRegistros"
          />
          <small class="text-muted d-block mb-3"
            >{{ selecionados.length }} selecionado(s). Limite de 100 por
            cópia.</small
          >
        </template>
        <p v-else class="bg-light rounded p-3" role="status">
          <template v-if="carregando"
            ><b-spinner small /> Carregando cadastros da origem...</template
          >
          <template v-else
            >{{ total }} registro(s) disponível(is) para copiar.</template
          >
        </p>
      </div>
      <b-alert :show="!!erro" variant="danger">{{ erro }}</b-alert>
      <div v-if="origem && destino" class="border rounded p-3 mb-3">
        <div><strong>Copiar de:</strong> {{ nomeEmpresa(origem) }}</div>
        <div><strong>Inserir em:</strong> {{ nomeEmpresa(destino) }}</div>
        <div>
          <strong>Cadastros:</strong> {{ nomeModulo }} ·
          {{
            modo === "TODOS" ? "Todos" : selecionados.length + " selecionado(s)"
          }}
        </div>
      </div>
      <small class="text-muted d-block">
        Cadastros já existentes no destino serão preservados. Tributações e
        alíquotas necessárias serão copiadas junto.
      </small>
    </fieldset>

    <b-alert
      :show="!!resultado"
      variant="success"
      class="mt-3 mb-0"
      role="status"
    >
      <template v-if="resultado">
        Cópia concluída: {{ resultado.criados }} cadastro(s) criado(s),
        {{ resultado.existentes }} já existente(s) e
        {{ resultado.dependencias_criadas }} dependência(s) criada(s).
      </template>
    </b-alert>
    <template #modal-footer>
      <b-button
        variant="secondary"
        :disabled="copiando"
        @click="$refs.modal.hide()"
      >
        {{ resultado ? "Concluir" : "Cancelar" }}
      </b-button>
      <b-button
        v-if="!resultado"
        variant="primary"
        :disabled="!podeCopiar"
        @click="copiar"
      >
        <b-spinner v-if="copiando" small class="mr-1" />
        {{ copiando ? "Replicando..." : "Replicar cadastros" }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import EmpresaParceiroSelect from "./EmpresaParceiroSelect.vue";

export default {
  name: "CopiarCadastrosFiscais",
  components: { EmpresaParceiroSelect },
  data() {
    return {
      origem: null,
      destino: null,
      modulo: "NATUREZA",
      modo: "TODOS",
      modulos: [
        { value: "NATUREZA", text: "Naturezas de operação" },
        { value: "TRIBUTACAO", text: "Tributações" },
        { value: "PERFIL_TRIBUTARIO", text: "Perfis tributários" },
      ],
      modos: [
        { value: "TODOS", text: "Todos os registros" },
        { value: "SELECIONADOS", text: "Selecionar registros" },
      ],
      registros: [],
      selecionados: [],
      total: 0,
      pagina: 1,
      busca: "",
      carregando: false,
      consultaValida: false,
      copiando: false,
      erro: "",
      resultado: null,
      sequencia: 0,
    };
  },
  computed: {
    campos() {
      const campos = [{ key: "selecionar", label: "Selecionar" }];
      if (this.modulo === "NATUREZA") {
        campos.push({ key: "id", label: "Código" });
      }
      campos.push({ key: "descricao", label: "Descrição" });
      return campos;
    },
    mesmaEmpresa() {
      return (
        !!this.origem &&
        !!this.destino &&
        Number(this.origem.id_empresa) === Number(this.destino.id_empresa)
      );
    },
    nomeModulo() {
      return this.modulos.find((modulo) => modulo.value === this.modulo).text;
    },
    podeCopiar() {
      return (
        !!this.origem &&
        !!this.destino &&
        !this.mesmaEmpresa &&
        !this.copiando &&
        !this.carregando &&
        this.consultaValida &&
        !this.resultado &&
        (this.modo === "TODOS"
          ? this.total > 0
          : this.selecionados.length > 0 && this.selecionados.length <= 100)
      );
    },
  },
  watch: {
    origem: "reiniciarRegistros",
    modulo: "reiniciarRegistros",
    modo: "reiniciarRegistros",
    destino() {
      this.resultado = null;
    },
  },
  beforeDestroy() {
    this.invalidarConsulta();
  },
  methods: {
    abrir(cliente) {
      if (!cliente || !cliente.id_empresa || this.copiando) return;
      this.invalidarConsulta();
      this.origem = null;
      this.destino = {
        id_empresa: Number(cliente.id_empresa),
        nome: cliente.cliente || cliente.nome || cliente.nome_fantasia,
        cnpj_cpf: cliente.cnpj || cliente.cnpj_cpf,
      };
      this.modulo = "NATUREZA";
      this.modo = "TODOS";
      this.registros = [];
      this.selecionados = [];
      this.total = 0;
      this.consultaValida = false;
      this.busca = "";
      this.erro = "";
      this.resultado = null;
      this.$refs.modal.show();
    },
    nomeEmpresa(empresa) {
      return `${empresa.nome || empresa.nome_fantasia || "Empresa"} · #${
        empresa.id_empresa
      }`;
    },
    invalidarConsulta() {
      this.sequencia++;
      this.carregando = false;
    },
    impedirFechamento(event) {
      if (this.copiando) event.preventDefault();
    },
    reiniciarRegistros() {
      this.invalidarConsulta();
      this.registros = [];
      this.selecionados = [];
      this.total = 0;
      this.consultaValida = false;
      this.pagina = 1;
      this.busca = "";
      this.resultado = null;
      this.erro = "";
      if (this.origem) this.carregarRegistros();
    },
    async carregarRegistros(pagina = 1) {
      if (!this.origem) return;
      const sequencia = ++this.sequencia;
      this.carregando = true;
      this.consultaValida = false;
      this.erro = "";
      this.registros = [];
      try {
        const { data } = await this.$http.get(
          "/revenda/cadastros-fiscais/registros",
          {
            params: {
              empresa_origem: Number(this.origem.id_empresa),
              modulo: this.modulo,
              busca: this.busca,
              page: pagina,
              per_page: 20,
            },
          }
        );
        if (sequencia !== this.sequencia) return;
        this.registros = data.data;
        this.total = data.total;
        this.consultaValida = true;
        this.pagina = data.current_page;
      } catch (error) {
        if (sequencia === this.sequencia) {
          this.erro = this.mensagemErro(
            error,
            "Não foi possível carregar os cadastros. Tente novamente."
          );
        }
      } finally {
        if (sequencia === this.sequencia) this.carregando = false;
      }
    },
    mensagemErro(error, padrao) {
      const data = error.response && error.response.data;
      if (data && data.errors)
        return Object.values(data.errors).flat().join(" ");
      return (data && data.message) || padrao;
    },
    async copiar() {
      if (!this.podeCopiar) return;
      this.copiando = true;
      this.erro = "";
      try {
        const payload = {
          empresa_origem: Number(this.origem.id_empresa),
          empresa_destino: Number(this.destino.id_empresa),
          modulo: this.modulo,
        };
        if (this.modo === "SELECIONADOS")
          payload.registros = this.selecionados.map(Number);
        const { data } = await this.$http.post(
          "/revenda/cadastros-fiscais/copiar",
          payload
        );
        this.resultado = data.data;
      } catch (error) {
        this.erro = this.mensagemErro(
          error,
          "Não foi possível copiar os cadastros. Tente novamente."
        );
      } finally {
        this.copiando = false;
      }
    },
  },
};
</script>
